import React, {useEffect, useMemo, useRef, useState} from 'react'
import Select, {ActionMeta, components, MenuListProps, MultiValue, StylesConfig} from 'react-select'
import {Form} from 'react-bootstrap'
import {useI18n} from '../Utils/_helpers/useI18n'
import {
  useLazyGetVehicleTypeOptionsPublicQuery,
  useLazyGetVehicleTypeOptionsQuery,
} from '../../services/cellcast.v2'
import {VehicleTypeOption} from '../../services/cellcast.v2.types'
import {CreateVehicleTypeOptionModal} from '../pages/UserPages/vehicle-type-options/components/CreateVehicleTypeOptionModal'
import './Style/InputVehicleTypeOption.scss'

type VehicleTypeOptionSelectOption = {
  value: string
  label: string
}

type InputVehicleTypeOptionProps = {
  value: string[]
  onChange: (value: string[]) => void
  className?: string
  error?: string
  name?: string
  userIdForPublicList?: string
  allowCreate?: boolean
  allowedIds?: string[]
}

const DEFAULT_QUERY = {page: 1, limit: 200}

const InputVehicleTypeOption: React.FC<InputVehicleTypeOptionProps> = ({
  value,
  onChange,
  className,
  error,
  name,
  userIdForPublicList,
  allowCreate = false,
  allowedIds,
}) => {
  const {t} = useI18n()

  const [getPublicList] = useLazyGetVehicleTypeOptionsPublicQuery()
  const [getPrivateList] = useLazyGetVehicleTypeOptionsQuery()

  const [availableOptions, setAvailableOptions] = useState<VehicleTypeOption[]>([])
  const [openCreatePopup, setOpenCreatePopup] = useState(false)
  const [editingOption, setEditingOption] = useState<VehicleTypeOption | null>(null)
  const previousLabelsRef = useRef<string[]>([])

  useEffect(() => {
    let isMounted = true
    const load = async () => {
      try {
        const response = userIdForPublicList
          ? await getPublicList({userId: userIdForPublicList, ...DEFAULT_QUERY}).unwrap()
          : await getPrivateList(DEFAULT_QUERY).unwrap()

        if (isMounted && response?.data?.items) {
          setAvailableOptions(response.data.items)
        }
      } catch (e) {
        if (isMounted) {
          // keep silent; caller can show fallback UI
        }
      }
    }
    load()
    return () => {
      isMounted = false
    }
  }, [getPublicList, getPrivateList, userIdForPublicList])

  const selectOptions = useMemo<VehicleTypeOptionSelectOption[]>(() => {
    const filtered = availableOptions.filter((o) => Boolean(o.id) && Boolean(o.name))
    const withIds = filtered as Array<VehicleTypeOption & {id: string; name: string}>

    const limited =
      allowedIds && allowedIds.length > 0
        ? withIds.filter((o) => allowedIds.includes(o.id))
        : withIds

    return limited.map((o) => ({value: o.id, label: o.name}))
  }, [availableOptions, allowedIds])

  const selectedOptions = useMemo(() => {
    return selectOptions.filter((opt) => value.includes(opt.value))
  }, [selectOptions, value])

  useEffect(() => {
    const labels = selectedOptions.map((o) => o.label)
    if (JSON.stringify(labels) !== JSON.stringify(previousLabelsRef.current)) {
      previousLabelsRef.current = labels
    }
  }, [selectedOptions])

  const handleChange = (
    newValue: MultiValue<VehicleTypeOptionSelectOption>,
    _actionMeta: ActionMeta<VehicleTypeOptionSelectOption>
  ) => {
    const selectedValues = (newValue as VehicleTypeOptionSelectOption[]).map((opt) => opt.value)
    onChange(selectedValues)
  }

  const customStyles: StylesConfig<VehicleTypeOptionSelectOption, true> = {
    option: (provided, state) => ({
      ...provided,
      padding: '10px 16px',
      cursor: state.isDisabled ? 'not-allowed' : 'pointer',
      backgroundColor: state.isDisabled
        ? '#F5F8FA'
        : state.isSelected
        ? '#f0ebff'
        : state.isFocused
        ? '#f8f5ff'
        : 'white',
      color: state.isDisabled ? '#B5B5C3' : state.isSelected ? '#7950f3' : '#3F4254',
    }),
    control: (provided) => ({
      ...provided,
      backgroundColor: '#f5f8fa',
      border: '1px solid #E4E6EF',
      borderRadius: '0.475rem',
      minHeight: 45,
      boxShadow: 'none',
      '&:hover': {
        border: '1px solid #E4E6EF',
      },
    }),
    multiValue: (provided) => ({
      ...provided,
      backgroundColor: '#ffffff',
      borderRadius: '6px',
      padding: '2px',
    }),
    multiValueLabel: (provided) => ({
      ...provided,
      fontSize: '13px',
    }),
    multiValueRemove: (provided) => ({
      ...provided,
      color: '#7950f3',
      '&:hover': {
        backgroundColor: '#7950f3',
        color: 'white',
      },
    }),
    menu: (provided) => ({
      ...provided,
      borderRadius: '8px',
      boxShadow: '0px 0px 50px 0px rgba(82, 63, 105, 0.15)',
    }),
  }

  const MenuList = (props: MenuListProps<VehicleTypeOptionSelectOption, true>) => {
    return (
      <div className='promo-select'>
        <div className='promo-select-content'>{props.children}</div>
        {allowCreate && !userIdForPublicList ? (
          <div
            className='promo-select-create-button d-flex align-items-center gap-2 justify-content-center cursor-pointer'
            onClick={(e) => {
              e.preventDefault()
              e.stopPropagation()
              setEditingOption(null)
              setOpenCreatePopup(true)
            }}
          >
            <i className='bi bi-plus-lg text-primary fw-bold'></i>
            <p className='text-primary fw-bold'>
              {t('RAW_CONTACT_COLLECTION.CREATE_NEW') || 'Create new'}
            </p>
          </div>
        ) : null}
      </div>
    )
  }

  const refreshPrivate = async () => {
    const response = await getPrivateList(DEFAULT_QUERY).unwrap()
    if (response?.data?.items) setAvailableOptions(response.data.items)
  }

  return (
    <div className='input-promo-setting input-vehicle-type-option m-0 p-0'>
      <Form.Group>
        <Select<VehicleTypeOptionSelectOption, true>
          value={selectedOptions}
          components={{MenuList}}
          isMulti
          className={`select ${className || ''}`}
          options={selectOptions}
          onChange={handleChange}
          styles={customStyles}
          name={name}
        />
        {error ? (
          <div className='fv-plugins-message-container mt-2'>
            <span role='alert' className='text-danger'>
              {error}
            </span>
          </div>
        ) : null}
      </Form.Group>

      {openCreatePopup ? (
        <CreateVehicleTypeOptionModal
          show={openCreatePopup}
          handleClose={() => setOpenCreatePopup(false)}
          vehicleTypeOption={editingOption}
          onSuccess={async () => {
            await refreshPrivate()
            setOpenCreatePopup(false)
          }}
        />
      ) : null}
    </div>
  )
}

export default InputVehicleTypeOption
